import {useCallback, useEffect, useRef, useState} from 'react';
import {useBlocker} from 'react-router-dom';
import {useMobile} from 'utils/hooks';
import {getPlatformBlockRules} from './platformRules';

/** Reason for the current blocking modal — null means no modal visible. */
export type BlockingReason =
  | 'keyboard_shortcut'
  | 'clipboard'
  | 'text_selection'
  | 'context_menu'
  | 'tab_return'
  | 'navigation'
  | 'drag_drop'
  | null;

export const BLOCKING_MESSAGES: Record<NonNullable<BlockingReason>, string> = {
  keyboard_shortcut: 'Esta função não está disponível no Modo Foco.',
  clipboard: 'Esta função não está disponível no Modo Foco.',
  text_selection: 'Esta função não está disponível no Modo Foco.',
  context_menu: 'Esta função não está disponível no Modo Foco.',
  tab_return:
    'Sua saída da aba ou aplicativo foi registrada e será reportada ao professor.',
  navigation: 'Você não pode sair durante o Modo Foco.',
  drag_drop: 'Esta função não está disponível no Modo Foco.',
};

/** Keys that are blocked when Focus Mode is active (used with Ctrl/Cmd). */
const BLOCKED_KEYS = new Set(['c', 'v', 'x', 'a']);

// Prevent duplicate counting when browsers emit quick visibility oscillations.
const RETURN_DEBOUNCE_MS = 800;

/** Prefixes allowed for step navigation. */
const ALLOWED_ACTIVITY_PREFIXES = [
  '/atividades/escrita',
  '/atividades/pesquisa',
  '/atividades/leitura',
  '/atividades/simulado',
];

export interface UseBlockUserActionsParams {
  isActive: boolean;
  /** Whether the Focus Mode timer has expired — releases all navigation blocks. */
  isExpired: boolean;
  /** ID of the current learning path (trilha query param value). */
  learningPathId: string | null;
  /** Emits lifecycle events for backend registration. */
  onUserEventFired?: (eventType: 'tab_leave' | 'tab_return') => void;
}

/**
 * useBlockUserActions
 *
 * Enforces Focus Mode restrictions when `isActive` is true:
 *   - Blocks Ctrl/Cmd + C / V / X / A (keyboard shortcuts)
 *   - Blocks copy/cut/paste events (desktop menus + mobile virtual keyboard)
 *   - Blocks text selection start on non-editable areas (mobile long-press)
 *   - Blocks right-click context menu
 *   - Intercepts browser tab close / refresh / external navigation (beforeunload)
 *   - Detects tab leave / return via visibilitychange
 *   - Blocks internal React Router navigation away from the current trilha
 *
 * Returns `blockingMessage` (string | null) and `dismissBlockingModal()` so
 * the caller can render a modal and close it.
 */
function useBlockUserActions({
  isActive,
  isExpired,
  learningPathId,
  onUserEventFired,
}: UseBlockUserActionsParams) {
  const {isMobile, isIos, isAndroid, isPWA} = useMobile();

  const {
    shouldHandleKeyboardShortcuts,
    shouldHandleClipboardEvents,
    shouldHandleSelectStart,
    shouldHandleContextMenu,
    shouldTrackVisibilityChange,
    shouldBlockInternalNavigation,
    shouldHandleDragEvents,
  } = getPlatformBlockRules({
    isMobile,
    isIos,
    isAndroid,
    isPWA,
    userAgent: navigator.userAgent,
  });

  const [blockingReason, setBlockingReason] = useState<BlockingReason>(null);

  // Timestamp of when the student last left the tab.
  const tabLeaveTimeRef = useRef<number | null>(null);
  const lastHandledReturnRef = useRef<number | null>(null);

  // Stable ref to the latest blocker so dismissBlockingModal can call reset().
  const blockerRef = useRef<ReturnType<typeof useBlocker> | null>(null);

  const showModal = useCallback((reason: NonNullable<BlockingReason>) => {
    setBlockingReason(reason);
  }, []);

  const dismissBlockingModal = useCallback(() => {
    if (blockerRef.current?.state === 'blocked') {
      blockerRef.current.reset();
    }
    setBlockingReason(null);
  }, []);

  // ─── Keyboard shortcuts ───────────────────────────────────────────────────

  useEffect(() => {
    if (!isActive || isExpired || !shouldHandleKeyboardShortcuts) {
      return undefined;
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (!BLOCKED_KEYS.has(e.key.toLowerCase())) return;

      e.preventDefault();
      e.stopPropagation();
      showModal('keyboard_shortcut');
    }

    // Use capture phase so the listener runs before the editor handles the event.
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isActive, isExpired, shouldHandleKeyboardShortcuts, showModal]);

  // ─── Clipboard (copy / cut / paste) ───────────────────────────────────────

  useEffect(() => {
    if (!isActive || isExpired || !shouldHandleClipboardEvents) {
      return undefined;
    }

    function handleClipboardEvent(e: ClipboardEvent) {
      e.preventDefault();
      e.stopPropagation();
      showModal('clipboard');
    }

    document.addEventListener('copy', handleClipboardEvent, true);
    document.addEventListener('cut', handleClipboardEvent, true);
    document.addEventListener('paste', handleClipboardEvent, true);

    return () => {
      document.removeEventListener('copy', handleClipboardEvent, true);
      document.removeEventListener('cut', handleClipboardEvent, true);
      document.removeEventListener('paste', handleClipboardEvent, true);
    };
  }, [isActive, isExpired, shouldHandleClipboardEvents, showModal]);

  // ─── Text selection start (long-press selection on mobile) ───────────────

  useEffect(() => {
    if (!isActive || isExpired || !shouldHandleSelectStart) {
      return undefined;
    }

    function isEditableTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      if (target.isContentEditable) return true;
      const tag = target.tagName.toLowerCase();
      return tag === 'input' || tag === 'textarea';
    }

    function handleSelectStart(e: Event) {
      // Keep native selection/caret behavior in editable controls.
      if (isEditableTarget(e.target)) return;

      e.preventDefault();
      e.stopPropagation();
      showModal('text_selection');
    }

    document.addEventListener('selectstart', handleSelectStart, true);
    return () =>
      document.removeEventListener('selectstart', handleSelectStart, true);
  }, [isActive, isExpired, shouldHandleSelectStart, showModal]);

  // ─── Context menu ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isActive || isExpired || !shouldHandleContextMenu) {
      return undefined;
    }

    function handleContextMenu(e: MouseEvent) {
      e.preventDefault();
      e.stopPropagation();
      showModal('context_menu');
    }

    document.addEventListener('contextmenu', handleContextMenu, true);
    return () =>
      document.removeEventListener('contextmenu', handleContextMenu, true);
  }, [isActive, isExpired, shouldHandleContextMenu, showModal]);

  // ─── Drag and drop ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isActive || isExpired || !shouldHandleDragEvents) {
      return undefined;
    }

    function handleDragEvent(e: DragEvent) {
      e.preventDefault();
      e.stopPropagation();
      showModal('drag_drop');
    }

    document.addEventListener('dragstart', handleDragEvent, true);
    document.addEventListener('drag', handleDragEvent, true);
    document.addEventListener('drop', handleDragEvent, true);
    document.addEventListener('dragover', handleDragEvent, true);

    return () => {
      document.removeEventListener('dragstart', handleDragEvent, true);
      document.removeEventListener('drag', handleDragEvent, true);
      document.removeEventListener('drop', handleDragEvent, true);
      document.removeEventListener('dragover', handleDragEvent, true);
    };
  }, [isActive, isExpired, shouldHandleDragEvents, showModal]);

  // ─── Tab visibility (Alt+Tab / other tab) ────────────────────────────────

  useEffect(() => {
    if (!isActive || isExpired || !shouldTrackVisibilityChange) {
      return undefined;
    }

    function handleVisibilityChange() {
      const now = Date.now();

      if (document.hidden) {
        tabLeaveTimeRef.current = now;
        onUserEventFired?.('tab_leave');
      } else if (tabLeaveTimeRef.current !== null) {
        // Some mobile browsers fire duplicated return events.
        if (
          lastHandledReturnRef.current !== null &&
          now - lastHandledReturnRef.current < RETURN_DEBOUNCE_MS
        ) {
          return;
        }

        tabLeaveTimeRef.current = null;
        lastHandledReturnRef.current = now;
        onUserEventFired?.('tab_return');
        showModal('tab_return');
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [
    isActive,
    isExpired,
    onUserEventFired,
    shouldTrackVisibilityChange,
    showModal,
  ]);

  // ─── React Router navigation blocker ─────────────────────────────────────
  //
  // Allow only same LP step navigation:
  //   1. The destination must be within an allowed /atividades/* pathname.
  //   2. The ?trilha= value must match the current learning path ID.
  // Anything else (different LP, unrelated pathname, missing param) is blocked.

  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  const isExpiredRef = useRef(isExpired);
  isExpiredRef.current = isExpired;

  const learningPathIdRef = useRef(learningPathId);
  learningPathIdRef.current = learningPathId;

  const blocker = useBlocker(
    useCallback(
      ({nextLocation}) => {
        if (!shouldBlockInternalNavigation) return false;
        if (!isActiveRef.current) return false;
        if (isExpiredRef.current) return false;
        if (nextLocation.state?.focusModeComplete) return false;
        const nextSearch = new URLSearchParams(nextLocation.search);
        const nextLearningPathId = nextSearch.get('trilha');
        const nextPathname = nextLocation.pathname;

        const isSameLearningPath =
          nextLearningPathId === learningPathIdRef.current;
        const isAllowedPathname = ALLOWED_ACTIVITY_PREFIXES.some((prefix) =>
          nextPathname.startsWith(prefix),
        );

        return !(isSameLearningPath && isAllowedPathname);
      },
      [shouldBlockInternalNavigation],
    ),
  );

  blockerRef.current = blocker;

  const {state: blockerState} = blocker;

  useEffect(() => {
    if (blockerState === 'blocked') {
      showModal('navigation');
    }
  }, [blockerState, showModal]);

  const blockingMessage = blockingReason
    ? BLOCKING_MESSAGES[blockingReason]
    : null;

  return {blockingMessage, dismissBlockingModal};
}

export default useBlockUserActions;
